"use client";

import { useEffect } from "react";
import cn from "classnames";
import { useAudioRecorder } from "react-audio-voice-recorder";

import {
  StopPlayer,
  PausePlayer,
  PlayPlayer,
  ReplayPlayer,
  MicrophoneIcon,
  UploadOutlineIcon,
  LoadingSpinnerIcon,
} from "../AscIcons";
import styles from "./AscVoiceRecorder.module.scss";
import { useRef, useState } from "react";
import AscAudioPlayer from "../AscAudioPlayer/AscAudioPlayer";

import { IconButton } from "./AscVoiceRecorder.Button";
import { AscAudioRecorderVisualizer } from "./AscVoiceRecorder.Visualizer";
import { AscVoiceRecorderGuide } from "./AscVoiceRecorder.Guide";

export type AscVoiceRecorderProps = {
  onSubmit: (blob: Blob) => void;
  value: string | Blob;
  onChange: (blob: Blob | null) => void;
  className?: string;
  uploadingAudio?: boolean;
  showGuide?: boolean;
  showRetryButton?: boolean;
  showUploadButton?: boolean;
};

export default function AscVoiceRecorder({
  onSubmit,
  value,
  onChange,
  className,
  uploadingAudio,
  showGuide = true,
  showRetryButton = true,
  showUploadButton = true,
}: AscVoiceRecorderProps) {
  const containerRef = useRef(null);
  const [isAudioPaused, setAudioPaused] = useState(false);
  const controller = useAudioRecorder({
    echoCancellation: true,
    noiseSuppression: true,
  });

  const handleStartStopRecording = () => {
    setAudioPaused(false);
    if (controller.isRecording) {
      controller.stopRecording();
    } else {
      controller.startRecording();
    }
  };

  const handleRestartRecording = () => {
    onChange(null);
    setAudioPaused(false);
    controller.startRecording();
  };

  const handlePlayPauseRecording = () => {
    controller.togglePauseResume();
    if (controller.isPaused) {
      setAudioPaused(false);
    } else {
      setAudioPaused(true);
    }
  };

  useEffect(() => {
    setAudioPaused(true);
    if (controller.recordingBlob) {
      onChange(
        controller.recordingBlob.slice(
          0,
          controller.recordingBlob.size,
          "audio/webm"
        )
      );
      controller.recordingBlob = undefined;
    }
  }, [controller.recordingBlob]);

  return (
    <div
      ref={containerRef}
      data-testid="asc-voice-recorder"
      className={cn(styles.ground, className)}
    >
      {value && !controller.isRecording ? (
        <div className={styles.audioPlayer}>
          {showUploadButton && typeof value !== "string" && (
            <IconButton
              size="medium"
              onClick={() => !uploadingAudio && onSubmit(value)}
            >
              {uploadingAudio ? (
                <LoadingSpinnerIcon stroke="blue" />
              ) : (
                <UploadOutlineIcon />
              )}
            </IconButton>
          )}
          {showRetryButton && !uploadingAudio && (
            <IconButton size="medium" onClick={handleRestartRecording}>
              <ReplayPlayer />
            </IconButton>
          )}
          <AscAudioPlayer
            src={typeof value === "string" ? value : URL.createObjectURL(value)}
            className={styles.audioPlayerElement}
            onPause={() => setAudioPaused(true)}
            onPlay={() => setAudioPaused(false)}
          />
        </div>
      ) : (
        <div className={styles.audioWrapper}>
          <div className={styles.controller}>
            {controller.isRecording && (
              <IconButton size="medium" onClick={handlePlayPauseRecording}>
                {controller.isPaused ? <PlayPlayer /> : <PausePlayer />}
              </IconButton>
            )}
            <IconButton size="large" onClick={handleStartStopRecording}>
              {controller.isRecording ? <StopPlayer /> : <MicrophoneIcon />}
            </IconButton>
          </div>
          <AscAudioRecorderVisualizer controller={controller} />
        </div>
      )}

      {showGuide && (
        <AscVoiceRecorderGuide
          {...{
            isFileTypeString: typeof value === "string",
            isPaused: controller.isPaused || isAudioPaused,
            isRecording: controller.isRecording,
            hasAudioSource: !!value,
            showRetryButton,
          }}
        />
      )}
    </div>
  );
}
